import React, { useState, useEffect, useCallback } from 'react'
import { HiPlusCircle, HiTrash, HiCheck, HiX, HiArrowLeft, HiSparkles, HiPencil, HiCurrencyDollar } from 'react-icons/hi'
import { toast } from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError, getDeviceId } from '../utils/api'
import logger from '../utils/logger'
import CurrencySelector, { CURRENCIES } from './poll/CurrencySelector'
import OptionsTagInput from './poll/OptionsTagInput'
import {
  trackReviewPageViewed, trackReviewTitleEdited, trackReviewChoiceEdited,
  trackReviewChoiceDeleted, trackReviewChoiceAdded, trackPollConfirmed,
  trackErrorPollConfirm, trackErrorChoiceEdit,
  trackErrorChoiceDelete, trackErrorTitleEdit, trackErrorChoiceAdd
} from '../utils/analytics'
import usePageTitle from '../hooks/usePageTitle'
import SkeletonLoader from './SkeletonLoader'

function MenuPollReviewPage() {
  usePageTitle('Review Poll')
  const { id: pollId } = useParams()
  const navigate = useNavigate()
  const [poll, setPoll] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // Title editing state
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState('')
  const [titleError, setTitleError] = useState('')

  // Choices state
  const [choices, setChoices] = useState([])
  const [originalChoices, setOriginalChoices] = useState([]) // Track original values for change detection
  const [editingChoiceId, setEditingChoiceId] = useState(null)
  const [choiceValue, setChoiceValue] = useState('')
  const [choiceError, setChoiceError] = useState('')

  // Add choice state
  const [newChoiceText, setNewChoiceText] = useState('')
  const [addingChoice, setAddingChoice] = useState(false)
  const [addChoiceError, setAddChoiceError] = useState('')

  // Currency state (poll-level)
  const [currency, setCurrency] = useState(null)

  // Helper to get currency symbol
  const getCurrencySymbol = () => {
    const curr = CURRENCIES.find(c => c.code === currency)
    return curr ? curr.symbol : ''
  }

  // Update choice price
  const handleChoicePriceChange = (choiceId, newPrice) => {
    setChoices(prev => prev.map(choice =>
      choice.id === choiceId
        ? { ...choice, price: newPrice === '' ? null : parseFloat(newPrice) || 0 }
        : choice
    ))
  }

  // Update choice options
  const handleChoiceOptionsChange = (choiceId, newOptions) => {
    setChoices(prev => prev.map(choice =>
      choice.id === choiceId
        ? { ...choice, options: newOptions.length > 0 ? newOptions : null }
        : choice
    ))
  }

  const fetchPoll = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      logger.info('Fetching poll for review', { pollId })
      const response = await api.get(`/api/polls/${pollId}`)

      setPoll(response.poll)
      setChoices(response.choices)
      // Store original values for change detection on confirm
      setOriginalChoices(response.choices.map(c => ({
        id: c.id,
        text: c.text,
        price: c.price,
        options: c.options
      })))
      setTitleValue(response.poll.title)
      setCurrency(response.poll.currency || null)

      logger.info('Poll fetched for review', { pollId, choiceCount: response.choices.length })
      trackReviewPageViewed(pollId, response.choices.length)
    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to load poll for review. Please try again.'

      logger.error('Failed to fetch poll for review', { error: errorMessage, pollId })
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [pollId])

  useEffect(() => {
    fetchPoll()
  }, [fetchPoll])

  const handleTitleSave = async () => {
    if (!titleValue.trim()) {
      setTitleError('Title cannot be empty')
      return
    }

    if (titleValue.length > 150) {
      setTitleError('Title must be 150 characters or less')
      return
    }

    // Skip API call if title hasn't actually changed
    if (titleValue.trim() === poll?.title) {
      setEditingTitle(false)
      return
    }

    setSaving(true)
    setTitleError('')

    try {
      logger.info('Updating poll title', { pollId, newTitle: titleValue.substring(0, 50) + '...' })

      const response = await api.request(`/api/polls/${pollId}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: titleValue.trim(), device_id: getDeviceId() })
      })

      setPoll(prev => ({ ...prev, title: response.title }))
      setEditingTitle(false)
      toast.success('Title updated successfully!')
      trackReviewTitleEdited(pollId)

      logger.info('Poll title updated', { pollId })
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to update title'
      setTitleError(errorMessage)
      trackErrorTitleEdit(pollId, errorMessage)
      logger.error('Failed to update poll title', { error: errorMessage, pollId })
    } finally {
      setSaving(false)
    }
  }

  const handleTitleCancel = () => {
    setTitleValue(poll?.title || '')
    setEditingTitle(false)
    setTitleError('')
  }

  const handleChoiceEdit = (choiceId, currentText) => {
    setEditingChoiceId(choiceId)
    setChoiceValue(currentText)
    setChoiceError('')
  }

  const handleChoiceSave = async () => {
    if (!choiceValue.trim()) {
      setChoiceError('Choice text cannot be empty')
      return
    }

    if (choiceValue.length > 100) {
      setChoiceError('Choice text must be 100 characters or less')
      return
    }

    // Skip API call if text hasn't actually changed
    const currentChoice = choices.find(c => c.id === editingChoiceId)
    if (currentChoice && choiceValue.trim() === currentChoice.text) {
      setEditingChoiceId(null)
      return
    }

    setSaving(true)
    setChoiceError('')

    try {
      logger.info('Updating choice text', { choiceId: editingChoiceId, newText: choiceValue.substring(0, 50) + '...' })

      const response = await api.request(`/api/choices/${editingChoiceId}`, {
        method: 'PATCH',
        body: JSON.stringify({ text: choiceValue.trim(), device_id: getDeviceId() })
      })

      setChoices(prev => prev.map(choice =>
        choice.id === editingChoiceId
          ? { ...choice, text: response.text, vote_count: response.vote_count, upvote_count: response.upvote_count, downvote_count: response.downvote_count }
          : choice
      ))
      setEditingChoiceId(null)
      toast.success('Choice updated successfully!')
      trackReviewChoiceEdited(pollId, editingChoiceId)

      logger.info('Choice text updated', { choiceId: editingChoiceId })
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to update choice'
      setChoiceError(errorMessage)
      trackErrorChoiceEdit(pollId, editingChoiceId, errorMessage)
      logger.error('Failed to update choice text', { error: errorMessage, choiceId: editingChoiceId })
    } finally {
      setSaving(false)
    }
  }

  const handleChoiceCancel = () => {
    setEditingChoiceId(null)
    setChoiceValue('')
    setChoiceError('')
  }

  const handleChoiceDelete = async (choiceId, choiceText) => {
    setSaving(true)

    try {
      logger.info('Deleting choice', { choiceId, choiceText: choiceText.substring(0, 50) + '...' })

      await api.request(`/api/choices/${choiceId}`, {
        method: 'DELETE',
        body: JSON.stringify({ device_id: getDeviceId() })
      })

      setChoices(prev => prev.filter(choice => choice.id !== choiceId))
      toast.success('Choice deleted successfully!')
      trackReviewChoiceDeleted(pollId, choiceId)

      logger.info('Choice deleted', { choiceId })
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to delete choice'
      toast.error(errorMessage)
      trackErrorChoiceDelete(pollId, choiceId, errorMessage)
      logger.error('Failed to delete choice', { error: errorMessage, choiceId })
    } finally {
      setSaving(false)
    }
  }

  const handleAddChoice = async (e) => {
    e.preventDefault()

    if (!newChoiceText.trim()) {
      setAddChoiceError('Please enter choice text')
      return
    }

    if (newChoiceText.length > 100) {
      setAddChoiceError('Choice text must be 100 characters or less')
      return
    }

    setAddingChoice(true)
    setAddChoiceError('')

    try {
      logger.info('Adding new choice to poll', { pollId, choiceText: newChoiceText.substring(0, 50) + '...' })

      const response = await api.post(`/api/polls/${pollId}/choices`, { text: newChoiceText.trim() })

      setChoices(prev => [...prev, response])
      setNewChoiceText('')
      toast.success('Choice added successfully!')
      trackReviewChoiceAdded(pollId)

      logger.info('Choice added to poll', { pollId, choiceId: response.id })
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to add choice'
      setAddChoiceError(errorMessage)
      trackErrorChoiceAdd(pollId, errorMessage)
      logger.error('Failed to add choice to poll', { error: errorMessage, pollId })
    } finally {
      setAddingChoice(false)
    }
  }

  const handleConfirm = async () => {
    setSaving(true)
    const deviceId = getDeviceId()

    try {
      logger.info('Saving poll settings before confirming', { pollId, currency })

      // Save currency if changed
      if (poll && currency !== poll.currency) {
        await api.request(`/api/polls/${pollId}`, {
          method: 'PATCH',
          body: JSON.stringify({ currency: currency, device_id: deviceId })
        })
        logger.info('Currency saved', { pollId, currency })
      }

      // Only update choices that have actually changed
      const changedChoices = choices.filter(choice => {
        const original = originalChoices.find(o => o.id === choice.id)
        if (!original) return true // New choice added during this session
        return (
          choice.text !== original.text ||
          choice.price !== original.price ||
          JSON.stringify(choice.options) !== JSON.stringify(original.options)
        )
      })

      logger.info('Updating changed choices', {
        totalChoices: choices.length,
        changedCount: changedChoices.length
      })

      // Update changed choices using batch API for better performance
      if (changedChoices.length > 0) {
        if (changedChoices.length >= 2) {
          // Use batch API for multiple choices
          try {
            await api.request(`/api/polls/${pollId}/choices/batch`, {
              method: 'PATCH',
              body: JSON.stringify({
                choices: changedChoices.map(c => ({
                  id: c.id,
                  text: c.text,
                  price: c.price,
                  options: c.options
                })),
                device_id: deviceId
              })
            })
            logger.info('Choices updated via batch API', { count: changedChoices.length })
          } catch (batchError) {
            // Fallback to individual requests if batch API fails
            logger.warn('Batch API failed, falling back to individual requests', { error: batchError.message })
            await Promise.all(changedChoices.map(choice =>
              api.request(`/api/choices/${choice.id}`, {
                method: 'PATCH',
                body: JSON.stringify({
                  text: choice.text,
                  price: choice.price,
                  options: choice.options,
                  device_id: deviceId
                })
              })
            ))
          }
        } else {
          // Single choice - use individual request
          const choice = changedChoices[0]
          await api.request(`/api/choices/${choice.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
              text: choice.text,
              price: choice.price,
              options: choice.options,
              device_id: deviceId
            })
          })
        }
        logger.info('Changed choices updated', { count: changedChoices.length })
      }

      toast.success('Poll settings saved!')
      const hasPrices = choices.some(c => c.price !== null && c.price !== undefined)
      trackPollConfirmed(pollId, choices.length, hasPrices)
      logger.info('Confirming poll review and navigating to poll', { pollId })
      navigate(`/poll/${pollId}`)
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to save settings'
      toast.error(errorMessage)
      trackErrorPollConfirm(pollId, errorMessage)
      logger.error('Failed to save poll settings', { error: errorMessage, pollId })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <SkeletonLoader variant="review-page" />
  }

  if (error) {
    return (
      <div style={{ padding: '2rem' }} className="fade-in">
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ color: 'var(--error-color)' }}>Error</h2>
          <p style={{ color: 'var(--error-color)', marginBottom: '1rem', fontSize: '1.125rem' }}>{error}</p>
          <div className="space-y-4" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <button className="btn btn-primary" onClick={fetchPoll} style={{ width: '100%' }}>
              Try Again
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/')} style={{ width: '100%' }}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content-container fade-in" style={{ paddingTop: '2rem', paddingBottom: '6rem', minHeight: '100vh' }}>
      {/* Header */}
      <div className="card fade-in-up" style={{
        maxWidth: '600px',
        margin: '0 auto 2rem',
        animationDelay: '0.1s',
        animationFillMode: 'both',
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <HiSparkles style={{ fontSize: '1.5rem', color: 'var(--accent-primary)' }} />
            <h2 style={{ margin: 0 }}>Review Your Poll</h2>
          </div>
        </div>
        <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
          Your menu has been analyzed and a poll was created. Review and edit the title and choices below, then confirm to start voting.
        </p>

        {/* Back to Home Button - Top Right of Header Box */}
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/')}
          aria-label="Back to Home"
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            width: '40px',
            height: '40px',
            minHeight: '40px',
            padding: 0,
            borderRadius: 'var(--radius-md)',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)'
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <HiArrowLeft style={{ fontSize: '1.125rem' }} />
        </button>
      </div>

      {/* Title Section */}
      <div className="card fade-in-up" style={{
        maxWidth: '600px',
        margin: '0 auto 2rem',
        animationDelay: '0.15s',
        animationFillMode: 'both'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem'
        }}>
          <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Poll Title</span>
        </div>

        {editingTitle ? (
          <div className="form-group">
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <input
                type="text"
                className="form-input"
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                placeholder="Enter poll title..."
                disabled={saving}
                maxLength={150}
                style={{ flex: 1 }}
              />
              <button
                className="btn btn-success btn-sm"
                onClick={handleTitleSave}
                disabled={saving || !titleValue.trim()}
                style={{ padding: '0.5rem' }}
              >
                <HiCheck style={{ fontSize: '1rem' }} />
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleTitleCancel}
                disabled={saving}
                style={{ padding: '0.5rem' }}
              >
                <HiX style={{ fontSize: '1rem' }} />
              </button>
            </div>
            {titleError && (
              <div className="form-error" style={{
                marginTop: '0.5rem',
                fontSize: '0.875rem'
              }}>
                {titleError}
              </div>
            )}
          </div>
        ) : (
          <div
            onClick={() => setEditingTitle(true)}
            style={{
              padding: '1rem',
              border: '2px dashed var(--border-color)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              backgroundColor: 'var(--bg-tertiary)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--accent-primary)'
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)'
              e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'
            }}
          >
            <div style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '0.25rem'
            }}>
              {poll?.title}
            </div>
            <div style={{
              fontSize: '0.875rem',
              color: 'var(--text-secondary)'
            }}>
              Click to edit title
            </div>
          </div>
        )}
      </div>

      {/* Currency Settings Section */}
      <div className="card fade-in-up" style={{
        maxWidth: '600px',
        margin: '0 auto 2rem',
        animationDelay: '0.175s',
        animationFillMode: 'both',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem'
        }}>
          <HiCurrencyDollar style={{ fontSize: '1.125rem', color: 'var(--accent-primary)' }} />
          <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Currency Settings</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>(optional)</span>
        </div>
        <p style={{
          fontSize: '0.875rem',
          color: 'var(--text-secondary)',
          marginBottom: '1rem',
          lineHeight: '1.5'
        }}>
          Select a currency to enable price display for choices. Leave empty if prices are not needed.
        </p>
        <CurrencySelector
          value={currency}
          onChange={setCurrency}
          disabled={saving}
        />
      </div>

      {/* Choices Section */}
      <div className="card fade-in-up" style={{
        maxWidth: '600px',
        margin: '0 auto 2rem',
        animationDelay: '0.2s',
        animationFillMode: 'both'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem'
        }}>
          <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
            Choices ({choices.length})
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {choices.map((choice) => (
            <div
              key={choice.id}
              style={{
                padding: '1rem',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-secondary)'
              }}
            >
              {editingChoiceId === choice.id ? (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                    <input
                      type="text"
                      className="form-input"
                      value={choiceValue}
                      onChange={(e) => setChoiceValue(e.target.value)}
                      placeholder="Enter choice text..."
                      disabled={saving}
                      maxLength={100}
                      style={{ flex: 1 }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleChoiceSave()
                        } else if (e.key === 'Escape') {
                          handleChoiceCancel()
                        }
                      }}
                    />
                    <button
                      className="btn btn-success btn-sm"
                      onClick={handleChoiceSave}
                      disabled={saving || !choiceValue.trim()}
                      style={{ padding: '0.5rem' }}
                    >
                      <HiCheck style={{ fontSize: '1rem' }} />
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleChoiceCancel}
                      disabled={saving}
                      style={{ padding: '0.5rem' }}
                    >
                      <HiX style={{ fontSize: '1rem' }} />
                    </button>
                  </div>
                  {choiceError && (
                    <div className="form-error" style={{
                      marginTop: '0.5rem',
                      fontSize: '0.875rem'
                    }}>
                      {choiceError}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem'
                  }}>
                    <div
                      onClick={() => handleChoiceEdit(choice.id, choice.text)}
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        fontSize: '1rem',
                        color: 'var(--text-primary)',
                        padding: '0.25rem 0'
                      }}
                    >
                      {choice.text}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleChoiceEdit(choice.id, choice.text)}
                        disabled={saving}
                        title="Edit choice"
                        style={{
                          padding: '0.5rem',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          background: 'rgba(59, 130, 246, 0.1)',
                          color: 'var(--accent-primary)',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          opacity: saving ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!saving) {
                            e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)'
                            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)'
                            e.currentTarget.style.transform = 'scale(1.05)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)'
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                      >
                        <HiPencil style={{ fontSize: '1rem' }} />
                      </button>
                      <button
                        onClick={() => handleChoiceDelete(choice.id, choice.text)}
                        disabled={saving}
                        title="Delete choice"
                        style={{
                          padding: '0.5rem',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: 'var(--error-color)',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          opacity: saving ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!saving) {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)'
                            e.currentTarget.style.transform = 'scale(1.05)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                      >
                        <HiTrash style={{ fontSize: '1rem' }} />
                      </button>
                    </div>
                  </div>

                  {/* Price Input - Always shown so user can add prices without currency */}
                  <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{
                      fontSize: '0.8rem',
                      color: 'var(--text-muted)',
                      minWidth: '40px'
                    }}>
                      Price:
                    </span>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: 'var(--bg-tertiary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-sm)',
                      overflow: 'hidden',
                      flex: 1,
                      maxWidth: '150px'
                    }}>
                      {currency && (
                        <span style={{
                          padding: '0.4rem 0.5rem',
                          background: 'rgba(59, 130, 246, 0.1)',
                          color: 'var(--accent-primary)',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          borderRight: '1px solid var(--border-color)'
                        }}>
                          {getCurrencySymbol()}
                        </span>
                      )}
                      <input
                        type="number"
                        value={choice.price ?? ''}
                        onChange={(e) => handleChoicePriceChange(choice.id, e.target.value)}
                        placeholder="0.00"
                        disabled={saving}
                        min="0"
                        step="0.01"
                        style={{
                          flex: 1,
                          padding: '0.4rem 0.5rem',
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-primary)',
                          fontSize: '0.85rem',
                          outline: 'none',
                          width: '80px'
                        }}
                      />
                    </div>
                  </div>

                  {/* Options Tag Input */}
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{
                      fontSize: '0.8rem',
                      color: 'var(--text-muted)',
                      marginBottom: '0.35rem'
                    }}>
                      Options (e.g., sizes, variations):
                    </div>
                    <OptionsTagInput
                      options={choice.options || []}
                      onChange={(newOptions) => handleChoiceOptionsChange(choice.id, newOptions)}
                      disabled={saving}
                      maxTags={20}
                      maxLength={50}
                      placeholder="Add option..."
                    />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add Choice Form */}
        <form onSubmit={handleAddChoice} style={{ marginTop: '1.5rem' }}>
          <div className="form-group">
            <label htmlFor="new-choice" className="form-label" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <HiPlusCircle style={{ fontSize: '1rem' }} />
              Add New Choice
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                id="new-choice"
                type="text"
                className="form-input"
                placeholder="Enter new choice..."
                value={newChoiceText}
                onChange={(e) => setNewChoiceText(e.target.value)}
                disabled={addingChoice}
                maxLength={100}
                style={{ flex: 1 }}
              />
              <button
                type="submit"
                className={`btn btn-primary ${addingChoice ? 'btn-loading' : ''}`}
                disabled={addingChoice || !newChoiceText.trim()}
                style={{ padding: '0.75rem 1rem' }}
              >
                <HiPlusCircle style={{ fontSize: '1rem' }} />
                {addingChoice ? 'Adding...' : 'Add'}
              </button>
            </div>
            {addChoiceError && (
              <div className="form-error" style={{
                marginTop: '0.5rem'
              }}>
                {addChoiceError}
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Confirm Section - Modern sticky button */}
      <div style={{
        position: 'fixed',
        bottom: '1.5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: '600px',
        width: 'calc(100% - 3rem)',
        zIndex: 1000,
        paddingBottom: 'env(safe-area-inset-bottom)'
      }}>
        <button
          className={`btn btn-primary ${saving ? 'btn-loading' : ''}`}
          onClick={handleConfirm}
          disabled={saving || addingChoice}
          style={{
            width: '100%',
            padding: '1rem 1.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4), 0 4px 12px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            position: 'relative',
            overflow: 'hidden',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            if (!saving && !addingChoice) {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(59, 130, 246, 0.5), 0 6px 16px rgba(59, 130, 246, 0.4)'
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)'
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.4), 0 4px 12px rgba(59, 130, 246, 0.3)'
          }}
        >
          <HiCheck style={{ fontSize: '1.25rem' }} />
          Confirm Poll & Start Voting
        </button>
      </div>
    </div >
  )
}

export default MenuPollReviewPage
